import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Shield,
  Check,
  Clock,
  AlertCircle,
  Upload,
  FileText,
  Camera,
  CreditCard,
  MapPin,
  Loader2,
} from "lucide-react";
import { MainLayout } from "@/components/layout/main-layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { KycDocument } from "@shared/schema";

const documentTypes = [
  {
    id: "drivers_license",
    title: "Driver's License",
    description: "Front and back of your valid driver's license",
    icon: CreditCard,
  },
  {
    id: "passport",
    title: "Passport",
    description: "Photo page of your valid passport",
    icon: FileText,
  },
  {
    id: "selfie",
    title: "Selfie Verification",
    description: "Take a photo holding your ID",
    icon: Camera,
  },
  {
    id: "proof_of_address",
    title: "Proof of Address",
    description: "Utility bill or bank statement (last 3 months)",
    icon: MapPin,
  },
];

function getStatusInfo(status: string | undefined) {
  switch (status) {
    case "verified":
      return {
        color: "bg-chart-1/20 text-chart-1",
        icon: Check,
        label: "Verified",
      };
    case "submitted":
      return {
        color: "bg-chart-4/20 text-chart-4",
        icon: Clock,
        label: "Under Review",
      };
    case "rejected":
      return {
        color: "bg-destructive/20 text-destructive",
        icon: AlertCircle,
        label: "Rejected",
      };
    default:
      return {
        color: "bg-muted text-muted-foreground",
        icon: Upload,
        label: "Not Submitted",
      };
  }
}

function DocumentCard({
  type,
  document,
  onUpload,
  uploading,
}: {
  type: (typeof documentTypes)[0];
  document?: KycDocument;
  onUpload: () => void;
  uploading: boolean;
}) {
  const statusInfo = getStatusInfo(document?.status);
  const StatusIcon = statusInfo.icon;

  return (
    <Card className="hover-elevate">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <type.icon className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-2 mb-1">
              <h3 className="font-medium">{type.title}</h3>
              <Badge className={statusInfo.color}>
                <StatusIcon className="h-3 w-3 mr-1" />
                {statusInfo.label}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground mb-4">{type.description}</p>
            {document?.status === "rejected" && document.rejectionReason && (
              <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm mb-4">
                {document.rejectionReason}
              </div>
            )}
            {(!document || document.status === "rejected") && (
              <Button
                variant="outline"
                size="sm"
                onClick={onUpload}
                disabled={uploading}
                data-testid={`button-upload-${type.id}`}
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Upload className="h-4 w-4 mr-2" />
                )}
                Upload Document
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function VerificationPage() {
  const { user, refreshUser } = useAuth();
  const { toast } = useToast();
  const [uploadingType, setUploadingType] = useState<string | null>(null);

  const { data: documents, isLoading } = useQuery<KycDocument[]>({
    queryKey: ["/api/kyc/documents"],
  });

  const uploadMutation = useMutation({
    mutationFn: async (documentType: string) => {
      const res = await apiRequest("POST", "/api/kyc/documents", { documentType });
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Document Submitted",
        description: "Your document has been submitted for review.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/kyc/documents"] });
      refreshUser();
      setUploadingType(null);
    },
    onError: () => {
      toast({
        title: "Upload Failed",
        description: "Failed to upload document. Please try again.",
        variant: "destructive",
      });
      setUploadingType(null);
    },
  });

  const handleUpload = (documentType: string) => {
    setUploadingType(documentType);
    uploadMutation.mutate(documentType);
  };

  const getDocumentForType = (typeId: string) => {
    return documents?.find((d) => d.documentType === typeId);
  };

  const verifiedCount = documents?.filter((d) => d.status === "approved").length || 0;
  const progress = (verifiedCount / documentTypes.length) * 100;

  const overallStatus = user?.kycStatus || "pending";
  const overallStatusInfo = getStatusInfo(overallStatus);
  const OverallStatusIcon = overallStatusInfo.icon;

  return (
    <MainLayout title="Identity Verification" subtitle="Complete KYC to unlock all features">
      <div className="max-w-3xl space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <CardTitle>Verification Status</CardTitle>
              </div>
              <Badge className={overallStatusInfo.color}>
                <OverallStatusIcon className="h-3 w-3 mr-1" />
                {overallStatus === "verified" ? "Fully Verified" : overallStatus === "submitted" ? "Under Review" : "Incomplete"}
              </Badge>
            </div>
            <CardDescription>
              Complete identity verification to access all trading features
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <div className="flex items-center justify-between text-sm">
                <span>Verification Progress</span>
                <span>{verifiedCount} of {documentTypes.length} documents</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
            {overallStatus === "verified" ? (
              <div className="p-4 rounded-lg bg-chart-1/10 text-chart-1 flex items-center gap-3">
                <Check className="h-5 w-5" />
                <div>
                  <p className="font-medium">Verification Complete</p>
                  <p className="text-sm opacity-80">You have full access to all trading features</p>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg bg-muted/50 text-muted-foreground flex items-center gap-3">
                <AlertCircle className="h-5 w-5" />
                <div>
                  <p className="font-medium">Complete Verification</p>
                  <p className="text-sm">Submit the required documents to unlock all features</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <h2 className="text-lg font-medium">Required Documents</h2>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading documents...</div>
          ) : (
            <div className="grid gap-4">
              {documentTypes.map((type) => (
                <DocumentCard
                  key={type.id}
                  type={type}
                  document={getDocumentForType(type.id)}
                  onUpload={() => handleUpload(type.id)}
                  uploading={uploadingType === type.id}
                />
              ))}
            </div>
          )}
        </div>

        <Card>
          <CardContent className="p-6">
            <div className="text-center text-sm text-muted-foreground">
              <p className="mb-2">
                Your documents are processed securely using industry-standard encryption.
              </p>
              <p>
                Verification typically takes 1-2 business days. Need help?{" "}
                <a href="#" className="text-primary hover:underline">
                  Contact Support
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
